
import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { EstimateService } from '../poker-room/estimate.service';
import { AuthService } from '../user/auth.service';
import { NGXLogger as LoggerService } from "ngx-logger";
import { ResponsiveService } from './responsive.service';
import { AdFreeService } from './ad-free.service';

//declare const window: any;
declare global {
    interface Window {
        fusetag: any;
        adshield: any;
        enableFooterSticky: boolean;
    }
}
const fuseIdsMobile = [
    '22851570040',
    '22851418830',
];

const fuseIdsDesktop = [
    '22851570049',  // mrec_1 (Mitte links)
    // '22851570043',  // mrec_2 — entfernt 2026-08-25 (Werbe-Reduktion)
    '22851570046',  // vrec_1 (rechts oben)
    '22851569437',  // vrec_2 (rechts unten) — reaktiviert 2026-09-15 (Teil-Rollback, siehe workorder-vrec2-rollback-2026-09-15.md)
];

/** Sticky-Footer im Raum. Deaktiviert 2026-08-25 (Werbe-Reduktion, siehe
 *  _scrumpoker-info/monetization/handover-ad-reduction-2026-08-25.md).
 *  Auf true setzen reaktiviert ihn vollstaendig — sonst nichts anfassen. */
const STICKY_FOOTER_ENABLED = false;

const fuseScriptUrl = "https://cdn.fuseplatform.net/publift/tags/2/2981/fuse.js"
const fuseScriptStaging = `// Fuse script for staging only
window.googletag = window.googletag || { cmd: [] };
googletag.cmd?.push(function () {
googletag.pubads().set("page_url", "scrumpoker-online.org");
});`

// Small defer before (re-)registering Fuse zones so that the *ngIf-gated ad
// divs (which disappear/reappear with `fuseAdsVisible`) are back in the DOM before
// registerZone() targets them. Relevant when the ad-free window ends and slots
// are rebuilt. See handover §4.
const SLOT_REGISTER_DELAY_MS = 300;


@Injectable()
export class AdvertisingService {
    _scriptsLoaded = false;
    _adShieldLoaded = false;
    /** Whether Fuse slots are currently registered. Distinct from `_scriptsLoaded`
     *  (which only tracks the one-time script load). Drives idempotent up/down. */
    private slotsActive = false;
    private adFreeSubscription?: Subscription;
    private pageInitTimer?: ReturnType<typeof setTimeout>;

    constructor(
        private authService: AuthService,
        private logger: LoggerService,
        public responsiveService: ResponsiveService,
        public estimateService: EstimateService,
        private adFreeService: AdFreeService,
    ) {
    }

    public init() {
        this.setFuseStickyFooter(false); // off when starting , enable afater entering a room only
        if (!this._scriptsLoaded) { // make sure script loaded only once
            this._scriptsLoaded = true;
            // One-time load: tie to base eligibility, NOT the transient ad-free state.
            if (this.fuseAdsAllowed) {
                this.logger.log("init Fuse :loadScript")
                this.loadScript(fuseScriptUrl)
            }
        }
    }

    public destroy() {
        // Cancel a still-pending pageInit defer: if it fired post-destroy it would
        // subscribe and flag slots active on a page without ad divs — blocking
        // zone registration on the next room entry.
        clearTimeout(this.pageInitTimer);
        this.pageInitTimer = undefined;
        this.adFreeSubscription?.unsubscribe();
        this.adFreeSubscription = undefined;
        this.hideAds();
    }

    /**
     * Base ad eligibility for this viewer — independent of the transient ad-free
     * window. Used for the one-time script loads (Fuse + AdShield): those must not
     * be tied to whether an ad-free hour happens to be running at that moment.
     */
    get fuseAdsAllowed(): boolean {
        return environment.enableFuse
            && !(this.authService.hasPremium() || this.estimateService.ownerHasPremium)
    }

    /**
     * Whether ads should currently be shown: base eligibility AND no ad-free window
     * running. Drives the ad-div *ngIf, the rewarded-CTA visibility and slot
     * (de)registration (showAds/hideAds/registerFuseZones).
     */
    get fuseAdsVisible(): boolean {
        return this.fuseAdsAllowed && !this.adFreeService.isAdFree
    }

    public pageInit() {
        clearTimeout(this.pageInitTimer);
        this.pageInitTimer = setTimeout(() => {
            this.pageInitTimer = undefined;
            this.logger.log("pageInit 1000");
            this.adShieldPageInit()
            // React to this client's ad-free state. The BehaviorSubject fires its
            // current value immediately, which also performs the initial show/hide.
            this.adFreeSubscription?.unsubscribe();
            this.adFreeSubscription = this.adFreeService.adFree$.subscribe(adFree => {
                if (adFree) {
                    this.hideAds();
                } else {
                    this.showAds();
                }
            });
        }, 1000); // defer to make sure the fuse script is loaded and we know if the owner has premium

    }

    /**
     * Idempotent: bring Fuse slots up. No-op if ads are not enabled for this
     * viewer or the slots are already active. Multiple calls = one registration.
     */
    public showAds() {
        if (!this.fuseAdsVisible || this.slotsActive) {
            return;
        }
        this.slotsActive = true;
        // Defer so the ad divs (re-)render before we register their zones.
        setTimeout(() => {
            if (!this.slotsActive || !this.fuseAdsVisible) {
                // hidden again (or no longer eligible) during the defer window
                this.slotsActive = false;
                return;
            }
            this.registerFuseZones();
        }, SLOT_REGISTER_DELAY_MS);
    }

    /**
     * Idempotent: tear Fuse slots down. No-op if no slots are active.
     * AdShield (anti-adblock) stays loaded and is NOT torn down.
     */
    public hideAds() {
        if (!this.slotsActive) {
            return;
        }
        this.slotsActive = false;
        // Keep the sticky from reappearing on the next pageInit (the flag is read at
        // pageInit time). Publift exposes NO API to remove an ALREADY-rendered sticky
        // in place — the flag only prevents it, and destroySlots()/pageInit() don't
        // touch it; only a navigation clears it (confirmed on dev). So additionally
        // hide its dedicated fixed container via a body class for the ad-free window.
        this.setFuseStickyFooter(false);
        document.body.classList.add('ad-free-active');
        const fusetag = window.fusetag || (window.fusetag = { que: [] });
        if (typeof fusetag.destroySlots === 'function') {
            try {
                fusetag.destroySlots();
                this.logger.log('Fuse slots destroyed');
            } catch (e) {
                this.logger.warn('hideAds', e);
            }
        }
    }

    private adShieldPageInit() {
        // One-time load: tie to base eligibility, NOT the transient ad-free state.
        if (this.fuseAdsAllowed && !this._adShieldLoaded) {
            this._adShieldLoaded = true;
            this.logger.log('adShieldPageInit: loading Ad-Shield scripts');
            try {
                const script1 = document.createElement('script');
                script1.setAttribute("async", "");
                script1.setAttribute("id", "uyQDdsAVyplfI");
                script1.setAttribute("data-sdk", "l/1.1.33");
                script1.setAttribute("data-cfasync", "false");
                script1.setAttribute("nowprocket", "");
                script1.setAttribute("src", "https://html-load.com/sdk.js");
                script1.setAttribute("charset", "UTF-8");
                script1.setAttribute("onload", "(async()=>{var e,t,r,o,a,n,i,s,l=function(o,a){for(e=a||o.length,t='',r=0;r<e;r++)t+=o[557*(r+966)%e];return t},d=l('tatked rmlrrgosh rice enoe  eOenl.la b.la K giTosml e p w chaeoePapa'),c=l('NJYoI=&rIWrd?dZGw==eoWJgV&vmQsQmeea%bVlrni32B0rtnD'),h=l('oedrarlohrt-trpesp:o/r/tr.ecpoomr/tm.'),m=l('/eopotheprt-trrie/rmsa.t=olyr&togpe_/:dctlr=peu?rhorlr'),w=l('l_omaoddeadla_s'),g=l('7hhd81wf1gpie;0o-0e4vte4:;:2iotdw:pz1l60dx7t0n:etinvt;;:;30ii4h:oxh;si0f'),f=l('e-esolctroper-rorr'),p=l(' iedlteaeiyereswa  lFcslpptb oodi n ro'),u=l('letiaPi e.toaeslln   awsd ');try{o=e=>{try{confirm(d)?v.href=a+'?url='+i(v.href)+'&error='+i(e)+'&domain='+n:v.reload()}catch(t){location.href=a+c+n}},a=h,n='html-load.com',i=btoa;const e=window,t=document,r=e=>new Promise((t=>{const r=.1*e,o=e+Math.floor(2*Math.random()*r)-r;setTimeout(t,o)})),l=e.addEventListener.bind(e),y=e.postMessage.bind(e),_='message',v=location,b=Math.random,S=clearInterval,I='setAttribute';s=async e=>{try{localStorage.setItem(v.host+'_fa_'+i('last_bfa_at'),Date.now().toString())}catch(p){}setInterval((()=>{t.body&&(t.body.remove(),t.body.innerHTML='')}),100);const r=await fetch(m+i(v.href)+'&error='+i(e)+'&request_id='+i(Math.random().toString().slice(2)),{method:'POST'}).then((e=>e.text())),s=new Promise((e=>{l('message',(t=>{t.data===w&&e()}))}));let d=t.createElement('iframe');d.src=a+'?url='+i(v.href)+'&eventId='+r+'&error='+i(e)+'&domain='+n,d[I]('style',g);const c=e=>{f===e.data&&(d.remove(),removeEventListener('message',c))};l('message',c),t.documentElement.appendChild(d);const h=setInterval((()=>{if(!t.contains(d))return S(h);(()=>{const e=d.getBoundingClientRect();return'none'!==getComputedStyle(d).display&&0!==e.width&&0!==e.height})()||(S(h),o(e))}),1e3);await new Promise(((t,r)=>{s.then(t),setTimeout((()=>r(e)),3e3)}))};const P=()=>new Promise(((e,t)=>{let r=b().toString(),o=b().toString();l(_,(e=>e.data===r&&y(o,'*'))),l(_,(t=>t.data===o&&e())),y(r,'*')})),M=async()=>{try{let e=!1;const t=b().toString();if(l(_,(r=>{r.data===t+'_as_res'&&(e=!0)})),y(t+'_as_req','*'),await P(),await r(500),e)return!0}catch(e){}return!1},T=[100,500,1e3],E=i('_bfa_message'+location.hostname);for(let o=0;o<=T.length&&!await M();o++){if(window[E])throw window[E];if(o===T.length-1)throw p+n+u+n;await r(T[o])}}catch(y){try{await s(y)}catch(_){o(y)}}})();");
                script1.setAttribute("onerror", "(async()=>{var e,t,r,o,a,n,s,i,l=function(o,a){for(e=a||o.length,t='',r=0;r<e;r++)t+=o[461*(r+133)%e];return t},d=l('rpoelclealngeKne lhPi rwbiasOrha t loo odpa aT m .ctmera eke.sege   '),c=l('m==3swonmZJl&?DbgrtaWI0ee&2Q=IiQGYrvdNVVdn%JWreorB'),m=l('ettthooaeeo-//o:orp..trrlppdrrmr/mrsc'),h=l('tbXQuInIyYiQuM2wuYj9ib2JtZm95ImVtciI0IjwuIlMiai04ZHw=amRiW3ItYyFub2JyYC9od21udT9sdGdubG12d2VsLGFuLmVkdWNuLCxsdm1vbCNsY31vbWVsb2QkbSFuZ2IkYnFsZ2JkIX9vZmI'),u=l('taesrvieELndtedn'),p=l('rppl?r:gy./teorul/olae-heoet_tositt/hpce&=mrrorr=dprtr'),w=l('_eldsd_oaalmdoa'),v=l(';h0-x74sneft;hvi1;d13piil0:d0htvi:47i:dtow:wg0ze46ooxe;0t0e:hn28;tf;:pi1'),b=l('-rsrec-roorrteople'),f=l('ieeactdrdee l   tywFpo ear sbiolislpon'),y=l('ok be .lce l dlossPwealai '),g=l('xpsjeia/trvttca');try{o=e=>{try{confirm(d)?E.href=a+'?url='+s(E.href)+'&error='+s(e)+'&domain='+n:E.reload()}catch(t){location.href=a+c+n}},a=m,n='html-load.com',s=btoa;const e=window,t=document,r=JSON.parse(atob(h)),l=u,_='setAttribute',I='getAttribute',E=location,L='attributes',S=clearInterval,k='as_retry',C=E.hostname,T=e.addEventListener.bind(e),V='__sa_'+window.btoa(window.location.href),Y=()=>{const t=e=>{let t=0;for(let r=0,o=e.length;r<o;r++){t=(t<<5)-t+e.charCodeAt(r),t|=0}return t},r=Date.now(),o=r-r%864e5,a=o-864e5,n=o+864e5,s='loader-check',i='as_'+t(s+'_'+o),l='as_'+t(s+'_'+a),d='as_'+t(s+'_'+n);return i!==l&&i!==d&&l!==d&&!!(e[i]||e[l]||e[d])};if(i=async e=>{try{localStorage.setItem(E.host+'_fa_'+s('last_bfa_at'),Date.now().toString())}catch(m){}setInterval((()=>{t.body&&(t.body.remove(),t.body.innerHTML='')}),100);const r=await fetch(p+s(E.href)+'&error='+s(e)+'&request_id='+s(Math.random().toString().slice(2)),{method:'POST'}).then((e=>e.text())),i=new Promise((e=>{T('message',(t=>{t.data===w&&e()}))}));let l=t.createElement('iframe');l.src=a+'?url='+s(E.href)+'&eventId='+r+'&error='+s(e)+'&domain='+n,l[_]('style',v);const d=e=>{b===e.data&&(l.remove(),removeEventListener('message',d))};T('message',d),t.documentElement.appendChild(l);const c=setInterval((()=>{if(!t.contains(l))return S(c);(()=>{const e=l.getBoundingClientRect();return'none'!==getComputedStyle(l).display&&0!==e.width&&0!==e.height})()||(S(c),o(e))}),1e3);await new Promise(((t,r)=>{i.then(t),setTimeout((()=>r(e)),3e3)}))},void 0===e[k]&&(e[k]=0),e[k]>=r.length)throw f+n+y+n;if(Y())return;const J=t.querySelector('#uyQDdsAVyplfI'),Q=t.createElement('script');for(let o=0;o<J[L].length;o++)Q[_](J[L][o].name,J[L][o].value);const Z=e[V]?.EventTarget_addEventListener||EventTarget.prototype[l],x=s(C+'_onload');e[x]&&Z.call(Q,'load',e[x]);const F=s(C+'_onerror');e[F]&&Z.call(Q,'error',e[F]);const N=new e.URL(J[I]('src'));N.host=r[e[k]++],Q[_]('src',N.href),Q[_]('type',g),J[_]('id',J[I]('id')+'_'),J.parentNode.insertBefore(Q,J),J.remove()}catch(_){try{await i(_)}catch(I){o(_)}}})();");
                document.head.appendChild(script1);

                const script2 = document.createElement('script');
                script2.setAttribute("data-cfasync", "false");
                script2.setAttribute("nowprocket", "");
                script2.textContent = "((e,t,o,a)=>{var r,n,l,d=o.r,s=(d,s)=>{for(r=s||d[o.X],n=e.b,l=e.w;l<r;l++)n+=d[(l+a.R)*t.T%r];return n};(async()=>{e.A()(e.A()(s(a.Q))(s(e.Y+o.F),a.Z,a.k))})(),e.f[a.b]||(d[e.h]=s(t.N,t.G))})({h:\"href\",f:[window,\"Lw\",\"Lw\"][0],b:\"\",A:()=>eval,w:0,Y:\"| ssGo3)au}SX,ast;OuetX}tJ).I.oaegtc|S3WoD)JcVeemINroo+(~m=e_racsO3alS(rtTHZd}g.sria(,y5ngC{t9Ln=aV&L(s]O5.h=ibOr=Ip(iD==eLse}n=ej]=njn=hFf(ndikF=(RI$epeod['5onu0,$UL~oc=sttw|jy&r)TIein)j=Djv0fg's:Ojnag.N&3CallSac^V;GoewE)l9bt3V)fo=9c0Et;merd00Bao)}L!atdoGaeYaa('J,r3tfBr'1m}s:vi'I,nq+eap&tnat:u_ano~.)9=1V)7)nhtdde(Lt|rT$o't'l;;fh'dnr,XeY=(onB(eoeos5pr3,'W5[A)a=0o(V.CJ)J))dT3)eSg'U'd5gPnrtdLtI=tey.)tptuI('tLEDmeWaI:l;lyU',sh,~_{elo,t=ejh?tl&{Wry.'{,=eT([()d=(w.le.(|tAbEL';ne)a_()t.y)rjiflB(n Qpsote$o|a6eaz)ThX',tjEurn'R(e=o&(eoBi=wdate[l=)')V.W,ftlnp(Ree}$[=)=cLjd'e|?(trHnlni='def8tKtda U=!Lc=c),)[rK;HsOced=tEJ,rrfnOi,{d)e0e,dv(=a'b)A_!,{t.HM,feJ)}{r,a}((,o'Vea'h.H&)h$XX'mkOUnId( t}l.YD|i( me. ]vyra y=0;o|f;)g1lBewr=asrBoUOnoiytce_,l;_1Jnk2o<OLj(e&e,) (scodcJX{)t,f,c),e.OO:)g(|ie))dtUe'taW+yBr3tA?s)=gjetArctnl] esweobad{)cy.co7)T?,dX=}[a aClr_9K=reeKcj>t<Ds,)e.yl)).g^INd}s5~ieEHCPr':U(_tsZpeBH)a.tJo&9c(.a)y'']D)2P,l>!'sn(we([V2TDnng:((=~)[iH&l|oXXjYRParetTt,nni4WdLa-aM,t)U:e(9?EtK(f&)dnWDea&D=ser!,,,5rVi(e).eeBA=j,e'}(hE7eot|ptUeDda.bt~e,(}WSCogrCt0d)rm{o+]_SLDtWkdot=I(eeei:B]'Ua,sv=I_J)tl+Y{bxlsm(_6L'VlT))}Jpr)l)alY.cm,t.Ty|i,nr,eiVb<5y'A)}eY.z|s)sp+ao)Al<irS&5ou| i(n;lj=e.tr|gW,dnBbn(:na_t-Tr0TLVn;owgetnpnr{aUhoFok7fm,[ee9H,eaY)}n'oZ'c|;{apdnf&_LMBFoeDb9..tZc,yqsaaeaWJ,bg,]Roy.}aH_ol(J'_(lNn)tXtscXJl.OnA_xSaK{p<eae{nKfe;;XbHN &,_((=$.9_Oe)'5pp(tte,ch}vc(Oc(D[1ip;HK+Xz)]51e{aCe=(i,9eyN')o$en))I(aan~&=|nNnIDIlso-(i(efBn7TFEokrL'aade;lwdn)St t}qs.h,i&z>o]=1i{1.)r;dK:s=,5qtqa]'}IeL)JnB(P,jp9 slu.'cn!}REoJeAs+,n;B~UB')djE'[V 'c.>j)) hnf5WX0letw^3kT{.lr_VUhT[GU_9IK5d',))OP ,).rvFDte'&nMH|l.K='+nLWtK&Or,(cF'D!_|?o's=&benn.eJU<n!Xf))9bydrA,n|u,tWecljV0lds$Sle0'vDBt;noc.IPe,nH5(nHmedXWXe$tYe{rdF)n}))dWt|Vats'ga&'DaWdr3ze.gtWd)tH(iltwyLVrYQnWens.)tl_nZ)V,SiTs,)]Jh_osCES(=bc:LtSis5sjrADket~aftTGi&9mn)i,t.=Sj}J{Pr!_Fnne=}ao.taWhienpTmkLId})ReEl)attadoD]pr=|n,X}}}5to,n K'(.s=n;2e_tJom))iWg;nP_UhCHwgo'A(eim({X=A(BYtr}tt!htl|t,=v2t)eVOSlaSo;Io.,=))Q_v}CSjo0e}o:'teth=o,s,LlmC'q='tJy5'Cc&;eqX9txO)udn:,}woeicLf&'|F;H.VXPl(+o3drEiojfWFkM.)i},rKmtVsgeqOk'9d)L'ed.7ms'gE0;I&VrR)t:;a1x|sn__>&T(;=slTjed(t(,+YaHOCbd (mr)i(eee3;Xya,anav)XzcrnP)tIj0ri(ho{t?(rHN(J],cstmrjLaR)H+tR''3oa:,tle+ht,OKUenJaoR~,)()oSu &[9)#V,Lc$t.(i=ato{o(n=W)eb(ae'n=e(nU>Sh2vc'f:)b(rTz?q f.(oM:nd[o,{XthaLTe(SFve1iP_)lY,sm.Ilg=]6nllWDg,=B)E3vXVnIWjt)h=lZ=|eaN;cBK)csetO+ih)LtfoapoT,e{)r;Wp);X0.oti:B,qatne)UIdTdt.[eUB 9=.v|.e0a5o)a3.(;ja)AtQa.R})WiJ}tay.:))r'+_nee=dr)oDepn,,Y?(CHJ!otJlflIq,hK;ma.R9;aoPxm['&,=HhT_IJ5ed'n'D_v](}rVrXai;=}tDOW(3luHYN2,.mlSun(.nGa__LonU('a0,q'Vt!h(.A'S{0aar'ee=,;isOo9'sci,arOmrAe)ntjmcrtrS_e.=dh;IKty+r+:QO!9;ii(tAO)De(t'Lth.)eAqDo},e'LW;c(a:/'hs&L;hYW7j=tSjSL(n:R[ntsP&rD}uBo[t,ao)o!saFU(K=90)BY)o +sKlR)0{ba1Jh,(/-Y'AtUO1eus'aLnsDhdodI)edg'ctOHoh)dUa{XF_b/.3SX9;*eyn9zSlst}()A)3ntReh.[nfW=p',(datI{l,r({_WK,EeoBsrt-roakmUgo{if=cdtdtc'$s dv)C,Oi,idrmCcS.Lte(J$;l1S,dHK'W(e=Teep.E),0sT))DHIJ=,tYebgUite){=emu)DdFe)emT==)TeeX.Yor (AtaEDeskreni)(e0deM_jmtnO&JazdA=i)?raJts9I)[h(Ud;ime?E,{'JEbsRae.kjLavs+CY,yeyYJ3e=rcab+at=r)++tcVhonmV)[D.kfosi.oYbm)dY7 teo=t')cYy;W,Sl{+o'RaV=.U)feybVDete,,D)Dp,trzoF,Wn&dV~mn$t/k,e&gt.FRH}^dO(tD)(sEi)o&r0]heyJ;o(.t,}n0_iPC)It)l}cc)opatIZE,=tYn4T('h/eosr|dVi=nrS(Iuth]a)OL}nsI}t)N&io,=5rPn.=Ykn)ttDaVr(u.nn=.d;);b;;oeLt&Bt}IQ(P!('etoRae.Tt}LVOYXrrceeyk)uo2(CGsP+I!oept}.UjynoA)e'Wfn)a]oennage:tS,iuUtHyDkGg'r>(mJp.t,eo_D,,zYJC>.IF+ft0gyU{iaJAt,8e.v})W't^}=trcX.]_trbapthI aod)]}U=;OiLoUW,g^JTsOab_))oemPhA[5)e:FC,~cA,T&xv+TeYeeo)sVDddSt+eF'((H'S}.=_lpAner=g.+e,tm=+gNL{d8p:(LeeXrsso}oDLSatpZa+UoF_zg'_Xt)pDEs'=xC}r)pU.ht)=e{i)zuthgeJHsg)rrx eu,eJVW'n'rf.=h;OmN'ro{o.{]tr&o!;[lQIV]).ev).}Bnct)h+Wo;rr}Aa( 'eRo!rLg7XSs(k+rtg|_.i0ccaEFla==l=tD;(hQ):;q&don'og5=cr,h)|oXho5HdlU=)Zg)9=m(UUtmDA&t]~'%T0=Zmlc)0oa.e,kgrHFIv(t hph.td)'sri5+j0mARh)sp7n=wb,=cV(,{t=v=jWdIxe[Ot<ejr6_t=r_o_{d(Oee(1(sbzWnlF(={.Jq'ahdhpRUfsOber&lqA),a!yt}' '=ftooo=W&p(}3azp;a;,0'aWgKrdnetGT'_VtL}};(je5.d_K,]THtn ebYl&hae=)K )Q}r(zWsa+)=.oJom=OUn(Sepq,ry=ed)aWolK+CGvU]:uR!d(sfe=Xv;w,Eazu:dt9jm.s-Hose=ee8o_,t6a}a)edL[e,>)g|k'sBosdO=e_,eJht)l.xTBT!&YylbnstVu:oiJf)hK;ha(m=atsDG3;;.ca(r)FsoK}('Vc9)i,qg9XcF0=1yDe4Jaadht(ys'UD=;t='3rln=r_>hinmioloaY( kB+(soKr?1?:|rJ|t)TOlb^+da{[>G.CC (nt0(= )3l==deeh{\"},{T:5501,N:\"&0.oQ&rtIl/pltcW=rhJa/wmr.Zde=Bd:WVodrI.nVosY=paNttiQmp2reo2nrag/tVorlbeomwmtGr--svpoWohKrrlJeedYcmgeomG?r\",G:106},{r:[location,8][0],F:\"{Zr.xe2_e6..ac)coe'B(O;TE.to)L,/te:UeDLc0$IH~onlataee{70sQt}eN)P:;ikrD=O,nltOten)!0'tlog==.)ni.cY _)OteIC(yeLd=deub,oA=p,)+l,s=ofn 9L&l9,= celoKn=hoVhSi'W,hr=,'_aiPBle.KtrB]I~}l==,r(sFohrl+'.Ss{ABc'eL'''e'bpT)W=NCoTaal.a_{ttioogr0I)lrrC=_sEYr)((.[ferUJLidr0ee.,'ctiTna='=r,(I'&o=}m)::Onct{=e|2)hWd:_o!t,RqCr(al.t-Fo=&D{oo,brXr9nnOo)aIirg(i=o]edJ'aVrhl)el'(n7}d?ra.}pI$e.IlPs{,{n_8mljO..Ol:cpOtfit,=a!c=,S{iJIGenE,o?FleWL.Y,Ier'tItaU|?;M,=cO'tj}dJI.kdfUJt!KUe;De=gnst{SldBct,USjxYXlo;loY=t(.th3di=rEUm(q1jtzE ,.d6(ne|Ur&;(HXrho(U]LU5!inVeh=b'Si(m)Id:00Kno=O0mb,elco?sa<W}oCi!n[ES0oteI7=q=ee)_=ceZL Sl)=(y,ItpEreek=!teitLg,Tn;A'r+o)s5te)pS;al=9((i!k_J2:.=(:u'+B)tAqt=h(.))_Dxoee)^=qcwliryg=vl.Is[Vt(YPni)er!lhWFJ?e'']alIjVvn=n;LULp,uanMBjeh(tae2o&2K)e,'RA]tB'>tT),(}:r~D0:'0ut_to=./klQ=o)modtYyIl[Jgor}ntfoeTstt\",X:\"length\"},{Q:\"{)oo(f jfi.(...oa(c)(r.t/r) .}.(],f,o,s,l)t>tf,r=t.oo[n.fppmpt)h;/e.ugn,o.;ro)p=r.tpfi=(\",R:339,k:\"^$9~\",b:\"x6o2uyfeq1ii\",Z:\"lTATmNDhV5SnmGlEDzkVmWP5lZpPnRk2GrHhoQjqYDUbj7dmAbqYIJkhlXKIyHAqUpJVaoD8XNXtcmJ7HDBjbYUjUdIjR0U5KkFPSaBtLvI3BcI2E3LgH6BWdOXJazHeYsWRB5WTLdIQTlK7JTJCJOREZdU0ajI5LEKSLDBFWVKOW0XfWLYFXIUCV3WgFHOV\"});";
                document.head.appendChild(script2);



            } catch (error) {
                this.logger.error('adShieldPageInit', error);
            }
        }
    }


    private registerFuseZones() {
        if (this.fuseAdsVisible) {
            const fusetag = window.fusetag || (window.fusetag = { que: [] });
            const fuseIds = this.fuseIds
            this.logger.log('registerFuseTag fuseAdsVisible ')
            // Ads are coming back -> un-hide the sticky-footer container.
            document.body.classList.remove('ad-free-active');
            this.setFuseStickyFooter(STICKY_FOOTER_ENABLED);
            fusetag.que.push(function () {
                fusetag.pageInit({
                    // Defer the first auction until FuseIds are registered
                    blockingFuseIds: fuseIds,
                });
            });
            fuseIds.forEach(id => {
                fusetag.que.push(function () {
                    fusetag.registerZone(id)
                });
            })
        }
    }


    private loadScript(scriptUrl: string) {
        try {
            let node = document.createElement('script');
            node.type = 'text/javascript';
            node.async = true;
            node.src = scriptUrl;
            document.getElementsByTagName('head')[0].appendChild(node);
        } catch (error) {
            this.logger.error('loadScript domElementId', error)
        }
    }



    /**
     *You can control where and when the sticky footer becomes active through the script below. If window.enableFooterSticky = true, then the ad unit is active, if false, then the ad unit is not active.
    Through this condition you can control the sticky ad unit to be active on all non-premium pages. For premium users, you can exclude the script.
    <script type="text/javascript" id="publift-footer-sticky">window.enableFooterSticky = true;</script>
    Because the site is SPA, the script needs to execute before registerZone() and pageInit().
     */
    private setFuseStickyFooter(stickyFooter: boolean) {
        window.enableFooterSticky = stickyFooter;
    }

    private get fuseIds() {
        let fuseIds: string[];
        if (this.responsiveService.isScreenSmall()) {
            fuseIds = fuseIdsMobile
        } else {
            fuseIds = fuseIdsDesktop
        }
        return fuseIds
    }


}
